import { Edit as EditIcon, ExpandMore, ExpandLess, FolderOpen as FolderOpenIcon } from "@mui/icons-material";
import { Avatar, Box, Chip, IconButton, Tooltip, Typography, Link } from "@mui/material";
import React, { useState } from "react";
import { WorkItemTypeEnum } from "../../store/services/timelineApiRTK";
import { useWorkPanel } from "../../hooks/useWorkPanel";
import { formatSmartDateForWorkItem } from "../../utils/dateFormatters";
import {
  formatLastActionComment,
  formatLastActionResult,
  getCategoryColor,
  getWorkItemColor,
  getWorkItemIcon,
  getWorkItemTypeName,
  WORK_ITEM_CATEGORIES,
  WorkItemCategory,
} from "../../utils/workItemUtils";

export interface ActionPreviewProps {
  workItemType: WorkItemTypeEnum;
  title: string;
  description?: string | null;
  date?: string | null;
  customerName?: string;
  onAction?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  actionIcon?: React.ReactNode;
  actionTooltip?: string;
  showAction?: boolean;
  leftBorderColor?: string;
  isCompleted?: boolean;
  isActive?: boolean;
  isOverdue?: boolean;
  /** Use minimalistic single-row layout */
  compact?: boolean;
  /** Show customer avatar in compact mode */
  compactWithCustomerInfo?: boolean;
  /** Result data for completed work items */
  resultData?: string | null;
  /** If set, shows an icon to open the parent Work */
  workId?: number | null;
}

const WorkItemListItem: React.FC<ActionPreviewProps> = ({
  workItemType,
  title,
  description,
  date,
  customerName,
  onAction,
  actionIcon = <EditIcon fontSize="small" />,
  actionTooltip = "Redigera",
  showAction = true,
  leftBorderColor,
  isCompleted = false,
  isActive = false,
  isOverdue = false,
  compact = false,
  compactWithCustomerInfo = false,
  resultData,
  workId,
}) => {
  const [descriptionExpanded, setDescriptionExpanded] = useState(false);
  const { openWork } = useWorkPanel();
  const actionColor = getWorkItemColor(workItemType);
  const actionLabel = getWorkItemTypeName(workItemType);

  // Bestäm kategori — null om typen inte hör till någon av de två listorna
  // (t.ex. systemtyper som Z04, AvtalsBrott, HandleEmail m.fl.). I så fall
  // döljer vi kategori-chippet helt istället för att felaktigt markera dem
  // som "Planering".
  const category: WorkItemCategory | null = WORK_ITEM_CATEGORIES[
    WorkItemCategory.FOLLOWUP
  ].includes(workItemType)
    ? WorkItemCategory.FOLLOWUP
    : WORK_ITEM_CATEGORIES[WorkItemCategory.COMMUNICATION].includes(workItemType)
    ? WorkItemCategory.COMMUNICATION
    : null;
  // Enum-värdet är redan etiketten ("Planering" / "Mötesbokning").
  const categoryLabel = category;
  const categoryColor = category ? getCategoryColor(category) : null;

  // Process date information
  let dateInfo = null;
  if (date) {
    try {
      dateInfo = formatSmartDateForWorkItem(date);
    } catch {
      dateInfo = { display: date, color: "default" };
    }
  }

  // Process result information for completed work items
  const resultInfo = isCompleted && resultData ? {
    result: formatLastActionResult(resultData),
    comment: formatLastActionComment(resultData)
  } : null;

  // Check if description needs truncation (more than 5 lines or more than 300 characters)
  const maxCharacters = 300;
  const maxLines = 5;
  
  const shouldTruncateByLines = description && description.split('\n').length > maxLines;
  const shouldTruncateByLength = description && description.length > maxCharacters;
  const shouldTruncateDescription = shouldTruncateByLines || shouldTruncateByLength;
  
  let truncatedDescription = description;
  if (shouldTruncateDescription && !descriptionExpanded && description) {
    // Truncate by lines first
    const lines = description.split('\n').slice(0, maxLines);
    let result = lines.join('\n');
    
    // Then check if we need to truncate by character count
    if (result.length > maxCharacters) {
      result = result.substring(0, maxCharacters).trim();
      // Try to cut at last word boundary to avoid cutting words in half
      const lastSpace = result.lastIndexOf(' ');
      if (lastSpace > maxCharacters * 0.8) { // Only cut at word if we're keeping at least 80% of the text
        result = result.substring(0, lastSpace);
      }
    }
    truncatedDescription = result;
  }

  // Calculate date chip styles
  const getDateChipColor = (color: string) => {
    switch (color) {
      case "error":
        return "error.main";
      case "warning":
        return "warning.main";
      case "success":
        return "success.main";
      default:
        return "grey.400";
    }
  };

  // Compact layout - single row with all info
  if (compact || compactWithCustomerInfo) {
    return (
      <Box
        sx={{
          position: "relative",
          display: "flex",
          alignItems: "center",
          gap: 1,
          p: 1,
          bgcolor: "grey.50",
          borderRadius: 1,
          border: "1px solid",
          borderColor: "grey.300",
        }}
      >
        {/* Status box - smaller and positioned at start */}
        {(isCompleted || isActive || isOverdue) && (
          <Box
            sx={{
              fontSize: "0.6rem",
              fontWeight: 600,
              color: "white",
              bgcolor: isCompleted
                ? "success.main"
                : isOverdue
                ? "error.main"
                : "warning.main",
              px: 0.75,
              py: 0.25,
              borderRadius: 0.5,
              minWidth: "fit-content",
            }}
          >
            {isCompleted ? "S" : isOverdue ? "F" : "A"}
          </Box>
        )}

        {/* Work item icon (always shown, but smaller in compactWithCustomerInfo) */}
        <Avatar
          sx={{
            bgcolor: actionColor,
            width: compactWithCustomerInfo ? 20 : 24,
            height: compactWithCustomerInfo ? 20 : 24,
            "& .MuiSvgIcon-root": {
              fontSize: compactWithCustomerInfo ? 12 : 14,
            },
          }}
        >
          {getWorkItemIcon(workItemType)}
        </Avatar>

        {/* Title and description - truncated if too long */}
        <Typography
          variant="caption"
          fontWeight="600"
          sx={{
            flex: 1,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            color: "text.primary",
          }}
        >
          {title}
          {compactWithCustomerInfo && customerName && ` - ${customerName}`}
          {description && (
            <Typography
              component="span"
              variant="caption"
              sx={{
                fontStyle: "italic",
                fontWeight: 400,
                color: "text.secondary",
              }}
            >
              {` - ${description}`}
            </Typography>
          )}
          {resultInfo?.result && (
            <Typography
              component="span"
              variant="caption"
              sx={{
                fontWeight: 500,
                color: "success.main",
                ml: 1,
              }}
            >
              {` (${resultInfo.result})`}
            </Typography>
          )}
        </Typography>

        {/* Category chip (only in regular compact mode, och bara om typen har en kategori) */}
        {compact && !compactWithCustomerInfo && category && (
          <Chip
            label={categoryLabel}
            size="small"
            sx={{
              borderColor: actionColor,
              color: "white",
              bgcolor: categoryColor ?? undefined,
              fontWeight: 500,
              fontSize: "0.6rem",
              height: 20,
              "& .MuiChip-label": {
                px: 0.5,
              },
            }}
          />
        )}

        {/* Type chip - smaller */}
        <Chip
          label={actionLabel}
          size="small"
          variant="outlined"
          sx={{
            borderColor: actionColor,
            color: actionColor,
            fontWeight: 500,
            fontSize: "0.6rem",
            height: 20,
            "& .MuiChip-label": {
              px: 0.5,
            },
          }}
        />

        {/* Date chip - smaller */}
        {dateInfo && (
          <Chip
            label={dateInfo.display}
            size="small"
            variant="outlined"
            sx={{
              borderColor: getDateChipColor(dateInfo.color),
              color:
                dateInfo.color === "default"
                  ? "text.primary"
                  : getDateChipColor(dateInfo.color),
              bgcolor: "white",
              fontWeight: 600,
              fontSize: "0.6rem",
              height: 20,
              "& .MuiChip-label": {
                px: 0.5,
              },
            }}
          />
        )}

        {/* Action button - smaller and at the end */}
        {showAction && onAction && (
          <IconButton
            size="small"
            onClick={onAction}
            title={actionTooltip}
            sx={{
              width: 20,
              height: 20,
              bgcolor: "white",
              "&:hover": { bgcolor: "grey.100" },
              "& .MuiSvgIcon-root": {
                fontSize: 12,
              },
            }}
          >
            {actionIcon}
          </IconButton>
        )}

        {/* Open parent Work */}
        {workId && (
          <Tooltip title="Öppna ärende">
            <IconButton
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                openWork(workId);
              }}
              sx={{
                width: 20,
                height: 20,
                color: "primary.main",
                "&:hover": { bgcolor: "primary.50" },
                "& .MuiSvgIcon-root": {
                  fontSize: 14,
                },
              }}
            >
              <FolderOpenIcon />
            </IconButton>
          </Tooltip>
        )}
      </Box>
    );
  }

  // Default layout
  return (
    <Box
      sx={{
        position: "relative",
        display: "flex",
        alignItems: "center",
        gap: 2,
        p: 2,
        pt: 3,
        bgcolor: "grey.50",
        borderRadius: 1,
        border: "1px solid",
        borderColor: "grey.300",
        ...(leftBorderColor && {
          borderLeft: "4px solid",
          borderLeftColor: leftBorderColor,
        }),
      }}
    >
      <Box
        sx={{
          position: "absolute",
          top: -1,
          left: -1,
          borderRadius: 1,
          zIndex: 1,
          gap: 0.5,
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
        }}
      >
        {/* Status box - fixed position at top */}
        {(isCompleted || isActive || isOverdue) && (
          <Box
            sx={{
              fontSize: "0.65rem",
              fontWeight: 600,
              color: "white",
              bgcolor: isCompleted
                ? "success.main"
                : isOverdue
                ? "error.main"
                : "warning.main",
              px: 1,
              py: 0.25,
              borderRadius: 1,
              minWidth: "fit-content",
            }}
          >
            {isCompleted ? "Slutförd" : isOverdue ? "Försenad" : "Aktiv"}
          </Box>
        )}
        {category && (
          <Box
            sx={{
              fontSize: "0.65rem",
              fontWeight: 600,
              color: "white",
              bgcolor: categoryColor ?? undefined,
              borderColor: actionColor,
              px: 1,
              py: 0.25,
              borderRadius: 1,
            }}
          >
            {categoryLabel}
          </Box>
        )}
        <Box
          sx={{
            fontSize: "0.65rem",
            fontWeight: 600,
            borderColor: actionColor,
            color: actionColor,
            border: "1px solid",
            px: 1,
            py: 0.25,
            borderRadius: 1,
            display: "inline-block",
          }}
        >
          {actionLabel}
        </Box>
      </Box>
      {/* Action button in top right corner */}
      {showAction && onAction && (
        <IconButton
          size="small"
          onClick={onAction}
          title={actionTooltip}
          sx={{
            position: "absolute",
            top: 8,
            right: 8,
            width: 24,
            height: 24,
            bgcolor: "white",
            "&:hover": { bgcolor: "grey.100" },
            "& .MuiSvgIcon-root": {
              fontSize: 16,
            },
            zIndex: 1,
          }}
        >
          {actionIcon}
        </IconButton>
      )}

      <Avatar
        sx={{
          bgcolor: actionColor,
          width: 40,
          height: 40,
        }}
      >
        {getWorkItemIcon(workItemType)}
      </Avatar>

      <Box flex={1}>
        <Typography
          variant="body2"
          fontWeight="600"
          sx={{
            mb: 0.5,
            pr: 4,
            color: "text.primary",
            display: "-webkit-box",
            WebkitLineClamp: 2,
            WebkitBoxOrient: "vertical",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "normal",
          }}
        >
          {title}
          {customerName ? ` - ${customerName}` : ""}
        </Typography>

        {description && (
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: "pre-wrap", display: "block" }}>
              {truncatedDescription}
              {shouldTruncateDescription && !descriptionExpanded && "..."}
            </Typography>
            {shouldTruncateDescription && (
              <Link
                component="button"
                variant="caption"
                onClick={() => setDescriptionExpanded(!descriptionExpanded)}
                sx={{
                  mt: 0.5,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.5,
                  textDecoration: "none",
                  "&:hover": {
                    textDecoration: "underline"
                  }
                }}
              >
                {descriptionExpanded ? (
                  <>
                    Visa mindre
                    <ExpandLess fontSize="small" />
                  </>
                ) : (
                  <>
                    Läs mer
                    <ExpandMore fontSize="small" />
                  </>
                )}
              </Link>
            )}
          </Box>
        )}

        {resultInfo?.result && (
          <Typography 
            variant="caption" 
            sx={{ 
              color: "success.main", 
              fontWeight: 500,
              display: "block",
              mt: 0.5 
            }}
          >
            Resultat: {resultInfo.result}
          </Typography>
        )}

        {resultInfo?.comment && (
          <Typography 
            variant="caption" 
            sx={{ 
              color: "text.secondary",
              fontStyle: "italic",
              display: "block",
              mt: 0.25
            }}
          >
            Kommentar: {resultInfo.comment}
          </Typography>
        )}
      </Box>

      {/* Date on the right side */}
      {dateInfo && (
        <Box sx={{ textAlign: "right", pr: 1 }}>
          <Chip
            label={dateInfo.display}
            size="small"
            variant="outlined"
            sx={{
              borderColor: getDateChipColor(dateInfo.color),
              color:
                dateInfo.color === "default"
                  ? "text.primary"
                  : getDateChipColor(dateInfo.color),
              bgcolor: "white",
              fontWeight: 600,
              fontSize: "0.75rem",
              "& .MuiChip-label": {
                px: 1,
              },
            }}
          />
        </Box>
      )}

      {/* Open parent Work — bottom right corner, flush with card border-radius */}
      {workId && (
        <Tooltip title="Öppna ärende">
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              e.preventDefault();
              openWork(workId);
            }}
            sx={{
              position: "absolute",
              bottom: -1,
              right: -1,
              width: 28,
              height: 28,
              borderRadius: "8px 0 4px 0",
              color: "white",
              bgcolor: "primary.main",
              "&:hover": { bgcolor: "primary.dark" },
              "& .MuiSvgIcon-root": {
                fontSize: 16,
              },
            }}
          >
            <FolderOpenIcon />
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
};

export default WorkItemListItem;
